import type { ErrorInfo } from 'react';
import { reportErrorToService, addBreadcrumb } from './errorReporting';

function createFallbackErrorInfo(context: string): ErrorInfo {
  return { componentStack: context };
}

/**
 * Известные безвредные ошибки, которые не нужно логировать.
 *
 * ResizeObserver loop — стандартное предупреждение браузера, возникающее когда
 * callback ResizeObserver вызывает layout-изменения (например, setState → re-render),
 * которые триггерят повторное наблюдение в том же кадре.
 * Это не ошибка приложения — см. https://github.com/WICG/resize-observer/issues/38
 */
const IGNORED_ERROR_MESSAGES = [
  'ResizeObserver loop completed with undelivered notifications',
  'ResizeObserver loop limit exceeded',
];

export function handleGlobalError(event: ErrorEvent): void {
  // Пропускаем известные безвредные ошибки
  const message = event.message || '';
  if (IGNORED_ERROR_MESSAGES.some((ignored) => message.includes(ignored))) {
    event.stopImmediatePropagation();
    event.preventDefault();
    return;
  }

  const error = event.error instanceof Error
    ? event.error
    : new Error(message || 'Global error');

  addBreadcrumb({
    type: 'user-action',
    timestamp: Date.now(),
    message: `Unhandled error: ${error.message}`,
  });

  if (import.meta.env.PROD) {
    reportErrorToService({
      errorId: `GLOBAL_${Date.now()}`,
      timestamp: Date.now(),
      error,
      errorInfo: createFallbackErrorInfo('window.onerror'),
      userAgent: navigator.userAgent,
      url: window.location.href,
    });
  }
}

export function handleUnhandledRejection(event: PromiseRejectionEvent): void {
  const error = event.reason instanceof Error
    ? event.reason
    : new Error(typeof event.reason === 'string' ? event.reason : 'Unhandled promise rejection');

  addBreadcrumb({
    type: 'user-action',
    timestamp: Date.now(),
    message: `Unhandled rejection: ${error.message}`,
  });

  if (import.meta.env.PROD) {
    reportErrorToService({
      errorId: `PROMISE_${Date.now()}`,
      timestamp: Date.now(),
      error,
      errorInfo: createFallbackErrorInfo('unhandledrejection'),
      userAgent: navigator.userAgent,
      url: window.location.href,
    });
  }
}

export function registerGlobalErrorHandlers(): void {
  window.addEventListener('error', handleGlobalError);
  window.addEventListener('unhandledrejection', handleUnhandledRejection);
}
